import { useState, type FormEvent } from 'react'
import { ArrowRight, Menu, Moon, Sun, X } from 'lucide-react'
import { waitlistService } from '@/services/waitlistService'
import { useTheme } from '@/hooks/useTheme'

const SCROLL_OPTIONS: ScrollIntoViewOptions = { behavior: 'smooth', block: 'start' }

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView(SCROLL_OPTIONS)
}

const Navbar = () => {
  const [open, setOpen] = useState(false)
  const { resolvedTheme, setTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'

  const links = [
    { label: 'How it works', id: 'pain' },
    { label: 'FAQ', id: 'faq' },
  ]

  return (
    <nav className="fixed top-0 inset-x-0 z-50 px-6 py-4 backdrop-blur-md bg-bg-deepest/80 border-b border-border">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <div className="font-display text-xl font-bold tracking-tight">
          contxt<span className="text-brand-500">md</span>
        </div>

        <div className="hidden md:flex items-center gap-6">
          {links.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => scrollToId(l.id)}
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => scrollToId('waitlist')}
            className="px-4 py-2 bg-primary hover:bg-primary-hover rounded-md text-sm font-medium text-primary-foreground transition-colors"
            style={{ boxShadow: '0 2px 8px rgba(79, 128, 255, 0.25)' }}
          >
            Join waitlist
          </button>
          <button
            type="button"
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            aria-label="Toggle color theme"
            className="w-9 h-9 rounded-full border border-border bg-bg-elevated text-text-secondary flex items-center justify-center hover:border-border-hover hover:text-text-primary transition-colors"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>

        <div className="flex md:hidden items-center gap-3">
          <button
            type="button"
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            aria-label="Toggle color theme"
            className="w-9 h-9 rounded-full border border-border bg-bg-elevated text-text-secondary flex items-center justify-center"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            type="button"
            onClick={() => setOpen((p) => !p)}
            aria-label="Toggle navigation"
            className="text-text-primary"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden absolute top-full inset-x-0 bg-bg-deepest border-b border-border p-6 flex flex-col gap-4 shadow-2xl">
          {links.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => {
                setOpen(false)
                scrollToId(l.id)
              }}
              className="text-left text-base text-text-secondary hover:text-text-primary"
            >
              {l.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              scrollToId('waitlist')
            }}
            className="w-full py-3 bg-primary hover:bg-primary-hover rounded-md font-medium text-primary-foreground"
          >
            Join waitlist
          </button>
        </div>
      )}
    </nav>
  )
}

const Hero = () => (
  <section className="relative pt-36 pb-24 px-6 overflow-hidden">
    <div
      aria-hidden="true"
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] -z-10 rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(79, 128, 255, 0.25) 0%, transparent 70%)',
      }}
    />

    <div className="max-w-3xl mx-auto text-center relative">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-bg-elevated text-text-tertiary font-mono text-[11px] tracking-[0.12em] uppercase mb-8">
        For startups
      </div>

      <h1 className="font-display text-5xl md:text-6xl lg:text-[4rem] font-black tracking-[-0.04em] leading-[1.05] text-text-primary mb-6">
        One source of truth <span className="text-brand-500">for your company</span>.
      </h1>

      <p className="text-lg md:text-xl text-text-secondary leading-relaxed max-w-2xl mx-auto mb-10">
        Your pitch deck is in Google Slides. Your data room is in Dropbox. Your customer
        insights are in a voice note. Your Q1 plan is in someone&rsquo;s head. We pull it all
        into one structured, versioned context engine, so your AI tools, your investors,
        and your next hire all see the same company.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          type="button"
          onClick={() => scrollToId('waitlist')}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary hover:bg-primary-hover text-primary-foreground font-medium text-base transition-colors"
          style={{ boxShadow: '0 2px 8px rgba(79, 128, 255, 0.25)' }}
        >
          Join the waitlist <ArrowRight size={18} />
        </button>
        <button
          type="button"
          onClick={() => scrollToId('pain')}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-bg-surface hover:bg-bg-surface-hover text-text-primary font-medium text-base border border-border transition-colors"
        >
          See how it works
        </button>
      </div>
    </div>
  </section>
)

const PAIN_BULLETS = [
  {
    quote: '"The AI gave me a generic answer."',
    cause: 'Because your context was unstructured.',
  },
  {
    quote: '"The new person can\'t get up to speed."',
    cause: 'Because your institutional memory isn\'t portable.',
  },
  {
    quote: '"Investors keep asking the same questions."',
    cause: 'Because there\'s no single source of truth they can pull from.',
  },
] as const

const Pain = () => (
  <section id="pain" className="py-24 px-6 border-t border-border">
    <div className="max-w-4xl mx-auto">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-[-0.02em] text-text-primary mb-3 text-center">
        The mess every founder lives in
      </h2>
      <p className="text-text-secondary text-center mb-12 max-w-xl mx-auto">
        Three things you&rsquo;ve probably said in the last month.
      </p>

      <div className="grid md:grid-cols-3 gap-4">
        {PAIN_BULLETS.map((p) => (
          <div
            key={p.quote}
            className="bg-bg-elevated border border-border rounded-lg p-6 hover:border-border-hover transition-colors"
          >
            <p className="font-display text-lg font-semibold text-text-primary leading-snug mb-3">
              {p.quote}
            </p>
            <p className="text-sm text-text-secondary leading-relaxed">{p.cause}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
)

const CAPABILITIES = [
  'A startup-shaped context engine: team, product, market, customers, financials, investors, legal, technology, strategy, operations, partnerships.',
  'Multi-format ingestion. Drop in pitch decks, data rooms, voice notes, Slack threads, Google Docs. We handle parsing and transcription.',
  'Investor-ready exports. Pitch outline, due diligence packet, drops for Claude, Cursor, and NotebookLM.',
  'A structured mentorship layer. Every advisory session becomes a permanent, searchable artifact.',
  'One source of truth that updates as your company changes. Old facts get flagged. New content gets diffed.',
] as const

const WhatYoullGet = () => (
  <section className="py-24 px-6 bg-bg-primary border-t border-border">
    <div className="max-w-3xl mx-auto">
      <div className="font-mono text-[11px] tracking-[0.12em] uppercase text-brand-500 mb-3 text-center">
        What you&rsquo;ll get
      </div>
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-[-0.02em] text-text-primary mb-12 text-center">
        One source of truth your AI tools can actually read.
      </h2>

      <ul className="space-y-3">
        {CAPABILITIES.map((c, i) => (
          <li
            key={c}
            className="flex items-start gap-4 p-5 rounded-lg bg-bg-elevated border border-border"
          >
            <span className="font-mono text-xs text-brand-500 mt-1 shrink-0">
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="text-text-secondary leading-relaxed">{c}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
)

const TRANSFORMATIONS = [
  { from: '200 emails of customer feedback', to: 'a structured ICP packet' },
  { from: '38 weeks of meeting notes', to: 'a working operating system' },
  { from: 'A pitch deck and a data room', to: 'a due-diligence-ready context engine' },
] as const

const Proof = () => (
  <section className="py-20 px-6 border-t border-border">
    <div className="max-w-4xl mx-auto">
      <div className="font-mono text-[11px] tracking-[0.12em] uppercase text-text-tertiary mb-6 text-center">
        What we&rsquo;ll do for you
      </div>
      <p className="font-display text-xl md:text-2xl font-semibold tracking-[-0.02em] text-text-primary leading-snug text-center mb-10 max-w-2xl mx-auto">
        Drop in your raw stack. Walk away with structure.
      </p>

      <ul className="space-y-3 max-w-3xl mx-auto">
        {TRANSFORMATIONS.map((t) => (
          <li
            key={t.from}
            className="grid md:grid-cols-[1fr_auto_1fr] items-center gap-3 md:gap-5 p-5 rounded-lg bg-bg-elevated border border-border"
          >
            <span className="text-text-secondary leading-snug md:text-right">
              {t.from}
            </span>
            <span
              aria-hidden="true"
              className="font-mono text-brand-500 text-lg shrink-0 md:text-center"
            >
              &rarr;
            </span>
            <span className="text-text-primary leading-snug font-medium">
              {t.to}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </section>
)

const FAQ_ITEMS = [
  {
    q: 'Is my data private?',
    a: 'Yes. Section-level permissions, AI-only mode available, and redaction before ingestion. Your data never trains a foundation model.',
  },
  {
    q: 'Can I use this with Cursor, Claude, or ChatGPT?',
    a: 'All three. Plus NotebookLM, Slack, and any tool that speaks the Model Context Protocol.',
  },
  {
    q: 'How long until I get something useful out?',
    a: 'Target: under 45 minutes from signup to first exported context package.',
  },
  {
    q: 'Do I have to restructure my files before uploading?',
    a: 'No. Upload raw. We handle parsing, transcription, and structure on our side.',
  },
  {
    q: 'What happens when my company changes?',
    a: 'Versioning and freshness scoring are first-class. Old facts get flagged. New content gets diffed against what you already had.',
  },
] as const

const FaqRow = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between gap-4 py-5 text-left group"
        aria-expanded={open}
      >
        <span className="font-display text-lg font-semibold text-text-primary group-hover:text-brand-500 transition-colors">
          {q}
        </span>
        <span
          className="font-mono text-brand-500 text-xl shrink-0 transition-transform"
          style={{ transform: open ? 'rotate(45deg)' : 'rotate(0)' }}
          aria-hidden="true"
        >
          +
        </span>
      </button>
      {open && (
        <p className="text-text-secondary leading-relaxed pb-5 pr-10">{a}</p>
      )}
    </div>
  )
}

const Faq = () => (
  <section id="faq" className="py-24 px-6 bg-bg-primary border-t border-border">
    <div className="max-w-3xl mx-auto">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-[-0.02em] text-text-primary mb-12 text-center">
        Common questions
      </h2>
      <div>
        {FAQ_ITEMS.map((item) => (
          <FaqRow key={item.q} {...item} />
        ))}
      </div>
    </div>
  </section>
)

type WaitlistFormProps = {
  email: string
  isSubmitting: boolean
  isSubmitted: boolean
  error: string
  onEmailChange: (v: string) => void
  onSubmit: (e: FormEvent<HTMLFormElement>) => void
}

const WaitlistFooter = ({
  email,
  isSubmitting,
  isSubmitted,
  error,
  onEmailChange,
  onSubmit,
}: WaitlistFormProps) => (
  <footer
    id="waitlist"
    className="relative py-24 px-6 border-t border-border overflow-hidden"
  >
    <div
      aria-hidden="true"
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] -z-10 rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(79, 128, 255, 0.18) 0%, transparent 70%)',
      }}
    />

    <div className="max-w-2xl mx-auto text-center">
      <h2 className="font-display text-3xl md:text-5xl font-bold tracking-[-0.02em] text-text-primary mb-4">
        Stop re-explaining your startup.
      </h2>
      <p className="text-text-secondary text-base md:text-lg mb-10">
        We&rsquo;re onboarding founders one cohort at a time. Drop your email to get a
        spot.
      </p>

      {isSubmitted ? (
        <div
          className="inline-flex items-center gap-3 px-5 py-3 rounded-md text-sm font-medium"
          style={{
            background: 'rgba(0, 212, 170, 0.1)',
            border: '1px solid rgba(0, 212, 170, 0.2)',
            color: 'var(--success)',
          }}
        >
          You&rsquo;re on the list. We&rsquo;ll be in touch.
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
        >
          <input
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="you@company.com"
            required
            className="flex-1 px-4 py-3 rounded-md bg-bg-surface border border-border text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none transition-colors text-sm"
            aria-label="Email address"
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 rounded-md bg-primary hover:bg-primary-hover disabled:opacity-50 text-primary-foreground font-medium text-sm transition-colors"
            style={{ boxShadow: '0 2px 8px rgba(79, 128, 255, 0.25)' }}
          >
            {isSubmitting ? 'Joining...' : 'Join waitlist'}
          </button>
        </form>
      )}

      {error && !isSubmitted && (
        <p className="mt-4 text-sm" style={{ color: 'var(--error)' }}>
          {error}
        </p>
      )}

      <p className="mt-12 text-xs text-text-tertiary font-mono tracking-wide">
        © 2026 contxtmd
      </p>
    </div>
  </footer>
)

export function LandingPage() {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!email.trim() || isSubmitting) return

    setIsSubmitting(true)
    setError('')

    try {
      const result = await waitlistService.signup(email.trim())
      if (result.success) {
        setIsSubmitted(true)
      } else {
        setError(result.message ?? 'Something went wrong. Try again in a moment.')
      }
    } catch {
      setError('Something went wrong. Try again in a moment.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-bg-deepest text-text-primary font-sans">
      <Navbar />
      <main>
        <Hero />
        <Pain />
        <WhatYoullGet />
        <Proof />
        <Faq />
        <WaitlistFooter
          email={email}
          isSubmitting={isSubmitting}
          isSubmitted={isSubmitted}
          error={error}
          onEmailChange={setEmail}
          onSubmit={handleSubmit}
        />
      </main>
    </div>
  )
}
